import { Button, TextField } from '@spiaggeit/spit-ui'
import Cookies from 'js-cookie'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { generatePath, useNavigate } from 'react-router-dom'

import { LANDING_KIOSK_PATH } from '@/app/router/paths'
import { setupByUrl } from '@/store/commonActions'

import { licenseSlice } from '@/store/licenseSlice'
import { Logo } from '../../assets/icons/Logo'
import { useAppDispatch, useAppSelector } from '../../hooks/store'
import { useSetupKiosk } from '../../hooks/useSetupKiosk'
import { isKioskSelector } from '../../store/appSlice'
import {
  getKioskLocalSettings,
  kioskLocalSettingsSchema,
} from '../../utils/kiosk'

export function SettingsRoute() {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const isKiosk = useAppSelector(isKioskSelector)

  const navigate = useNavigate()

  const [license, setLicense] = useState('')
  const [kioskId, setKioskId] = useState('')

  const [errors, setErrors] = useState({ kioskId: false, license: false })
  useSetupKiosk()

  // initialize app
  useEffect(() => {
    if (!document.body.classList.contains('kiosk')) {
      document.body.classList.add('kiosk')
    }

    dispatch(setupByUrl(window.location.href))
  }, [dispatch])

  useEffect(() => {
    if (!isKiosk) return
    try {
      // if the cookie is already present, redirect to landing page
      const kioskLocalSettings = getKioskLocalSettings()

      if (kioskLocalSettings) {
        dispatch(
          licenseSlice.actions.setLicenseCode(kioskLocalSettings.license)
        )

        navigate(
          generatePath(LANDING_KIOSK_PATH, {
            license: kioskLocalSettings.license,
          })
        )
      }
    } catch {
      // NOOP
    }
  }, [isKiosk])

  if (!isKiosk) {
    return null
  }

  function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()

    const result = kioskLocalSettingsSchema.safeParse({
      kioskId,
      license,
    })

    if (result.success) {
      Cookies.set(
        import.meta.env.VITE_KIOSK_SETTINGS_COOKIE,
        JSON.stringify(result.data)
      )
      dispatch(licenseSlice.actions.setLicenseCode(result.data.license))
      navigate(
        generatePath(LANDING_KIOSK_PATH, {
          license: result.data.license,
        })
      )
      return
    }

    const { fieldErrors } = result.error.flatten()

    setErrors({
      kioskId: !!fieldErrors.kioskId,
      license: !!fieldErrors.license,
    })
  }

  return (
    <div className="mx-auto flex h-full max-w-96 flex-col items-center justify-center space-y-4 p-4">
      <Logo className="max-w-40 text-yellow-300" fill="currentColor" />

      <p>{t('kiosk.settings.message')}</p>

      <form className="w-full space-y-2" onSubmit={onSubmit}>
        <TextField
          errorMsg={errors.license ? t('kiosk.settings.requiredField') : null}
          id="license"
          label={t('kiosk.settings.license')}
          name="license"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setLicense(e.target.value)
          }}
          value={license}
        />

        <TextField
          errorMsg={errors.kioskId ? t('kiosk.settings.requiredField') : null}
          id="kioskID"
          label={t('kiosk.settings.kioskId')}
          name="kioskID"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setKioskId(e.target.value)
          }}
          value={kioskId}
        />

        <Button className="mt-8" fullWidth>
          {t('kiosk.settings.save')}
        </Button>
      </form>
    </div>
  )
}
